import { Box } from "@mui/material";
import {
	type User,
	onAuthStateChanged,
} from "firebase/auth";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { AppRouter } from "./AppRouter";
import { auth } from "./firebase/firebaseConfig";
import {
	useAppDispatch,
	useAppSelector,
} from "./redux/store";
import { getAllTenantsForUser } from "./redux/tenants/tenant.thunk";
import { UserAuthActions } from "./redux/user/user.slice";
import { userLoginThunk } from "./redux/user/user.thunk";
import { ResponsiveFrame } from "./responsive";
import {
	getTenantIdFromPathname,
	setActiveTenant as setStoredActiveTenant,
} from "./utils/tenantUtils";

import "react-toastify/dist/ReactToastify.css";

function App() {
	// Initialize dispatch and navigate hooks
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	// Get authStatus and isPasswordSet from Redux store
	const { authStatus, isPasswordSet, token } =
		useAppSelector((state) => state.user);
	const { activeTenant } = useAppSelector(
		(state) => state.tenant
	);

	// Define a callback function to login the Firebase user
	const loginFirebaseUser = useCallback(
		async (user: User) => {
			const token = await user.getIdToken();
			dispatch(UserAuthActions.setAuthenticated(token));
			dispatch(UserAuthActions.setMfaError(null));
		},
		[dispatch]
	);

	const initialSetup = useCallback(
		async (token: string) => {
			if (token) {
				const tenantIdFromUrl = getTenantIdFromPathname();
				if (tenantIdFromUrl) {
					setStoredActiveTenant(tenantIdFromUrl);
				}

				await Promise.all([
					dispatch(getAllTenantsForUser()),
					dispatch(userLoginThunk()),
				]);
			}
		},
		[dispatch]
	);

	useEffect(() => {
		if (token) {
			void initialSetup(token);
		}
	}, [token, initialSetup]);

	// Define a callback function to get the active user session
	const getActiveSession = useCallback(async () => {
		try {
			void dispatch(UserAuthActions.setAuthLoading(true));

			onAuthStateChanged(auth, async (user) => {
				try {
					if (user) {
						const tenantIdFromUrl =
							getTenantIdFromPathname();
						if (tenantIdFromUrl) {
							setStoredActiveTenant(tenantIdFromUrl);
						}

						if (!user.emailVerified) {
							await loginFirebaseUser(user);
							navigate("/create-password");
							void dispatch(
								UserAuthActions.setAuthLoading(false)
							);
						} else {
							await loginFirebaseUser(user);
						}
					} else {
						void dispatch(
							UserAuthActions.setAuthLoading(false)
						);
					}
				} catch (err) {
					console.error(err);
					void dispatch(
						UserAuthActions.setAuthLoading(false)
					);
				}
			});
		} catch (err) {
			console.error(err);
			void dispatch(UserAuthActions.setAuthLoading(false));
		}
	}, [loginFirebaseUser, dispatch, navigate]);

	// Call getActiveSession on component mount
	useEffect(() => {
		void getActiveSession();
	}, [getActiveSession]);

	// Redirect to create-password page if user is authenticated but password is not set
	useEffect(() => {
		if (authStatus.isLoggedIn && !isPasswordSet) {
			navigate("/create-password");
		}
	}, [authStatus.isLoggedIn, isPasswordSet, navigate]);

	return (
		<Box className="App">
			<ResponsiveFrame>
				<AppRouter key={activeTenant ?? "tenantless"} />
			</ResponsiveFrame>
			<ToastContainer
				autoClose={5000}
				position="top-center"
			/>
		</Box>
	);
}

export default App;
